import React, { createContext, useContext, useState } from "react"
import localStorage from "@locmod/local-storage"
import { constants } from "helpers"
import dayjs from "dayjs"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"

// Initialize plugins in correct order
dayjs.extend(utc) // UTC plugin must be extended before timezone
dayjs.extend(timezone)

export enum TimeFormat {
  HHmm24h = "HHmm24h",
  HHmm12h = "HHmm12h",
  hmmAMPM = "hmmAMPM",
  hhmmAMPM = "hhmmAMPM",
}

export const timeFormats = {
  [TimeFormat.HHmm24h]: "H:mm (24h)",
  [TimeFormat.HHmm12h]: "HH:mm (24h)",
  [TimeFormat.hmmAMPM]: "h:mm AM/PM",
  [TimeFormat.hhmmAMPM]: "hh:mm AM/PM",
}

const values = Object.keys(timeFormats)

type Context = {
  timeFormat: TimeFormat
  setTimeFormat: (timeFormat: TimeFormat) => void
  formatTime: (time: string | number) => string
}

const Context = createContext<Context | null>(null)

export const useTimeFormat = () => useContext(Context) as Context

// Helper function to convert time string to dayjs object
const parseTime = (time: string | number): dayjs.Dayjs => {
  // Use UTC for consistent server/client rendering
  // Only use timezone conversion on client side
  const isClient = typeof window !== "undefined"

  // If time is a number, assume it's a Unix timestamp in seconds
  if (typeof time === "number") {
    if (isClient) {
      const localTimezone = dayjs.tz.guess()
      return dayjs(time).tz(localTimezone)
    }
    return dayjs.utc(time)
  }

  // If time is already in HH:mm format
  if (/^\d{1,2}:\d{2}$/.test(time)) {
    const [hours, minutes] = time.split(":").map(Number)
    if (isClient) {
      const localTimezone = dayjs.tz.guess()
      return dayjs().tz(localTimezone).hour(hours).minute(minutes).second(0)
    }
    return dayjs.utc().hour(hours).minute(minutes).second(0)
  }

  // Try to parse as ISO string or other formats
  if (isClient) {
    const localTimezone = dayjs.tz.guess()
    return dayjs(time).tz(localTimezone)
  }
  return dayjs.utc(time)
}

export const TimeFormatProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Handle SSR by checking if we're on client side
  const isClient = typeof window !== "undefined"
  let initialValue = TimeFormat.HHmm24h

  if (isClient) {
    const storedValue = localStorage.getItem(
      constants.localStorageKeys.timeFormat
    ) as TimeFormat

    if (storedValue && values.includes(storedValue)) {
      initialValue = storedValue
    }
  }

  const [timeFormat, setActiveTimeFormat] = useState<TimeFormat>(initialValue)

  const setTimeFormat = (value: TimeFormat) => {
    if (isClient) {
      localStorage.setItem(constants.localStorageKeys.timeFormat, value)
    }
    setActiveTimeFormat(value)
  }

  const formatTime = (time: string | number) => {
    if (!time) {
      return "--"
    }

    const timeObj = parseTime(time)

    switch (timeFormat) {
      case TimeFormat.HHmm24h:
        return timeObj.format("H:mm")
      case TimeFormat.HHmm12h:
        return timeObj.format("HH:mm")
      case TimeFormat.hmmAMPM:
        return timeObj.format("h:mm A")
      case TimeFormat.hhmmAMPM:
        return timeObj.format("hh:mm A")
      default:
        return timeObj.format("H:mm")
    }
  }

  const context: Context = {
    timeFormat,
    setTimeFormat,
    formatTime,
  }

  return <Context.Provider value={context}>{children}</Context.Provider>
}
